import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useFormikContext, FormikContextType } from 'formik';
import { ErrorM } from './ErrorM';
import { Input } from './Input';
import ProgressIndicator from 'images/Progress_Indicator.gif';
import { useErrorToast } from 'hooks/useErrorToast';
import { PUBLIC_SITE_URL } from 'appConstants';

interface FormTextFieldProps<T> {
  field: string;
  label?: string;
  required: boolean;
  type?: string;
  hint?: string;
  optionalTag?: string;
  visible?: boolean;
  disabled?: boolean;
  placeholder?: string;
  labelPosition?: 'left' | 'top';
  value?: string;
  readOnly?: boolean;
  searchSchoolURN: (search_urn_string: string) => void;
  schoolUrnLoading: boolean;
  schoolUrnList: any;
  setSchoolUrnList: Dispatch<any>;
}

const FormSchoolURNTextField = function <T>({
  field,
  label,
  required,
  type,
  hint,
  optionalTag,
  visible,
  disabled,
  placeholder,
  labelPosition,
  value,
  readOnly,
  searchSchoolURN,
  schoolUrnLoading,
  schoolUrnList,
  setSchoolUrnList,
}: FormTextFieldProps<T>) {
  const formikContext = useFormikContext<FormikContextType<T>>();

  const checkClickOutside = (e: any) => {
    let schoolURNListContainer: any = document.getElementsByClassName('SchoolURNList')[0];
    if (schoolURNListContainer && !schoolURNListContainer.contains(e.target)) {
      closeUrnListDD();
    }    
  };

  const closeUrnListDD = () => {
    setSchoolUrnList([]);
    document.removeEventListener('click', checkClickOutside);
  };

  useEffect(() => {
    if(
      schoolUrnList &&
      schoolUrnList.length > 0
    ){
      document.addEventListener('click', checkClickOutside);
    }  
  }, [schoolUrnList]); 

  const prefillFormFromSchoolURN = (school_urn: any) => {
    formikContext.setFieldValue("schoolUrn", school_urn.urn ?? "");
    formikContext.setFieldValue("addressLine1", school_urn.addressLine1 ?? "");
    formikContext.setFieldValue("addressLine2", school_urn.addressLine2 ?? "");
    formikContext.setFieldValue("addressLine3", school_urn.addressLine3 ?? "");
    formikContext.setFieldValue("country", school_urn.country ?? "");
    formikContext.setFieldValue("county", school_urn.countyState ?? "");
    formikContext.setFieldValue("schoolname", school_urn.name ?? "");
    formikContext.setFieldValue("phoneNumber", school_urn.phoneNumber ?? "");
    formikContext.setFieldValue("townCity", school_urn.townCity ?? "");
    formikContext.setFieldValue("postcodeZipcode", school_urn.zipPostCode ?? "");
    
    closeUrnListDD();
  };

  const enterURNMessage = useErrorToast('Please enter an URN.');

  return (
    <div
      className={`FormBox FormSideBySide SchoolURNField ${
        visible === false ? 'HideField' : ''
      } ${labelPosition ? labelPosition : ''}`}
    >
      <div className="FormLabel">
        {label && (
          <label>
            {label}{' '}
            {!required && <span className="Optional">- {optionalTag}</span>}
          </label>
        )}
        <a 
          className="FindSchoolURNLink"
          href="https://get-information-schools.service.gov.uk/Search"
          target="_blank"
          rel="noreferrer"
        >
          Find my URN
        </a>
        {hint && <div className="FormHint">{hint}</div>}
      </div>
      <div className={"FormField" + (schoolUrnLoading ? " SchoolURNLoading" : "")}>      
        <div className="FieldLoadingPanel">
          <img src={ProgressIndicator} alt="Loading school URN list" />
        </div>
        <Input
          name={field}
          type={type ? type : 'text'}
          disabled={disabled}
          placeholder={placeholder}
          value={value}
          readOnly={readOnly ?? false}
          onKeyUp={(e: any) => { 
            closeUrnListDD();
            
            if(e.target.value){
              searchSchoolURN(e.target.value);
            }            
           }}
        />
        {schoolUrnList && schoolUrnList.length > 0 &&
          <div className="SchoolURNList">
            {schoolUrnList.map((urn: any, index: number) => {
              return (
                <div 
                  className="URNOpt" 
                  key={"School_URN_Opt_" + index}
                  onClick={() => prefillFormFromSchoolURN(urn)}
                >
                  {urn.name}
                </div>
              );
            })}
          </div>
        }
        <ErrorM name={field} />
        <p className="InfoTxt">If you could not find a URN for your school from the Department for Education platform, please <a href={`${PUBLIC_SITE_URL}/contact-us`} target="_blank" rel="noreferrer" className="ContactLink">contact</a> the Get Set 4 Education team.</p>
      </div>
    </div>
  );
};

export default FormSchoolURNTextField;
